import { useFormikContext } from "formik";
import { isEmpty, isEqual } from "lodash";
import { FC, useEffect, useMemo } from "react";

import { DEFAULT_MULTIVALUES } from "web/Layout/SubscriptionReceivers/SubscriptionReceiverForm/ActiveForm/constants";

import {
  ISubscriptionReceiverFormField,
  ISubscriptionReceiverFormValues,
  PhoneWithPrefixValue,
  SubscriptionActiveFormFieldType,
} from "web/types/Subscription";

const isPhoneWithPrefixValue = (
  value: unknown
): value is PhoneWithPrefixValue =>
  !!value &&
  typeof value === "object" &&
  "prefix" in value &&
  "phoneNumber" in value;

const isFieldFilled = (value: unknown) => {
  if (isPhoneWithPrefixValue(value)) {
    return !!value.phoneNumber;
  }
  if (value && typeof value === "object") {
    return !isEmpty(value);
  }
  return !!value || value === 0;
};

const getEmptyValue = (type: ISubscriptionReceiverFormField["type"]) => {
  if (type === SubscriptionActiveFormFieldType.CHECKBOX) return false;
  if (type === SubscriptionActiveFormFieldType.REQUIRE_PESEL) {
    return DEFAULT_MULTIVALUES[SubscriptionActiveFormFieldType.REQUIRE_PESEL];
  }
  if (type === SubscriptionActiveFormFieldType.PHONE_WITH_PREFIX) {
    return DEFAULT_MULTIVALUES[
      SubscriptionActiveFormFieldType.PHONE_WITH_PREFIX
    ];
  }
  return "";
};

export const getDisabledDependentFieldIds = (
  fields: ISubscriptionReceiverFormField[],
  values: ISubscriptionReceiverFormValues,
  excludedTypes: ISubscriptionReceiverFormField["type"][] = []
) => {
  const fieldsById = new Map(fields.map((field) => [field.id, field]));
  const disabledById = new Map<string, boolean>();

  const isDisabled = (fieldId: string, visitedIds: Set<string>): boolean => {
    const cachedValue = disabledById.get(fieldId);
    if (cachedValue !== undefined) return cachedValue;

    const field = fieldsById.get(fieldId);
    if (
      !field?.requiredWhenFilled ||
      excludedTypes.includes(field.type) ||
      visitedIds.has(fieldId)
    ) {
      return false;
    }

    const nextVisitedIds = new Set(visitedIds).add(fieldId);
    const dependencyIsDisabled = fieldsById.has(field.requiredWhenFilled)
      ? isDisabled(field.requiredWhenFilled, nextVisitedIds)
      : false;
    const disabled =
      dependencyIsDisabled || !isFieldFilled(values[field.requiredWhenFilled]);

    disabledById.set(fieldId, disabled);
    return disabled;
  };

  return new Set(
    fields
      .filter((field) => isDisabled(field.id, new Set()))
      .map((field) => field.id)
  );
};

export const ClearDisabledDependentFieldsEffect: FC<{
  fields: ISubscriptionReceiverFormField[];
  excludedTypes?: ISubscriptionReceiverFormField["type"][];
}> = ({ fields, excludedTypes = [] }) => {
  const { values, setFieldTouched, setFieldValue } =
    useFormikContext<ISubscriptionReceiverFormValues>();
  const disabledFieldIds = useMemo(
    () => getDisabledDependentFieldIds(fields, values, excludedTypes),
    [excludedTypes, fields, values]
  );

  useEffect(() => {
    fields.forEach((field) => {
      if (!disabledFieldIds.has(field.id)) return;

      const emptyValue = getEmptyValue(field.type);
      if (!isEqual(values[field.id], emptyValue)) {
        setFieldValue(field.id, emptyValue, false);
        setFieldTouched(field.id, false, false);
      }
    });
  }, [disabledFieldIds, fields, setFieldTouched, setFieldValue, values]);

  return null;
};
